export const translations = {
  en: {
    nav: {
      how: 'How it works',
      rates: 'Rates',
      faq: 'FAQ',
      apply: 'Apply now',
      track: 'Track',
    },
    track: {
      title: 'Track your application',
      sub: 'Enter the reference ID we shared after you applied.',
      inputLabel: 'Reference ID',
      placeholder: 'PDI-XXXXXXXX',
      button: 'Check status',
      checking: 'Checking…',
      notFound: 'No application found with this reference ID. Double-check it and try again.',
      appliedOn: 'Applied on',
      amount: 'Loan amount',
      tenure: 'Tenure',
      total: 'Total repayment',
      note: 'Note from our team',
      trackCta: 'Track your application',
      rejected: 'This application was declined. Reach out to support for details.',
      rejectedTitle: 'Declined',
      days: 'days',
      steps: ['Application received', 'Under review', 'Approved', 'Money disbursed'],
      checklistTitle: 'Approved — what happens next',
      checklist: [
        'E-sign your loan agreement — a secure link and an OTP arrive on your registered mobile (about 2 minutes).',
        'Keep your PAN, Aadhaar and bank account handy for disbursal.',
        'Money lands in your bank within ~10 minutes of e-sign, via UPI/IMPS.',
      ],
      checklistDocs: 'No more documents needed right now — our NBFC partner may occasionally request a salary slip or bank statement.',
      creditedTitle: 'Money sent to your bank',
      creditedAmount: 'Amount credited',
      repayOn: 'Repay on',
      repayMethod: 'Pay via UPI, netbanking, debit card or autopay — repayment takes two taps.',
      payNow: 'Pay now via UPI',
      copyVpa: 'Copy UPI ID',
      vpaCopied: 'UPI ID copied!',
    },
    hero: {
      badge: 'RBI-registered NBFC partners · 100% paperless',
      lines: ['Cash before', 'payday,', 'in minutes.'],
      sub: 'Salary advance from ₹5,000 to ₹1,00,000 straight to your bank. Digital KYC, transparent 0.1% daily interest, repayment on your salary day.',
      ctaApply: 'Apply for a loan',
      ctaHow: 'See how it works',
      estimate: 'Quick estimate',
      estimateHint: 'Drag to pick an amount — repay it on payday.',
      tenureLabel: 'Tenure',
      youRepay: 'You repay',
      dueOn: 'Due on',
      ctaThis: 'Get this amount',
      stats: [
        { v: '₹5K–₹1L', l: 'Loan range' },
        { v: '~10 min', l: 'Bank transfer' },
        { v: '7–90 days', l: 'Flexible tenure' },
      ],
      transferChip: '₹25,000 credited · 4:32 PM',
    },
    marquee: [
      'Instant disbursal',
      'Zero collateral',
      '100% digital KYC',
      'RBI-aligned NBFC partners',
      '₹5,000 to ₹1,00,000',
      '10-minute bank transfer',
      'No hidden charges',
    ],
    rates: {
      eyebrow: 'Transparent pricing',
      title: 'Know your exact cost. Before you apply.',
      sub: 'One simple daily rate — 0.1% per day (≈36.5% annualised). No surprises at repayment, ever.',
      amount: 'Loan amount',
      tenure: 'Tenure',
      days: 'days',
      interest: 'Interest (0.1% / day)',
      fee: 'Processing fee (2.5%, min ₹399)',
      gst: 'GST on processing fee (18%)',
      total: 'Total repayment',
      due: 'Repay on',
      apr: 'APR (interest component, annualised)',
      note: 'Illustrative pricing for RBI disclosure. Final sanction amount, rate and fees are communicated in the e-sign agreement issued by our NBFC partner before disbursal.',
      cta: 'Continue to application',
    },
    how: {
      eyebrow: 'How it works',
      title: 'From phone to bank in four steps.',
      steps: [
        { t: 'Two-minute application', d: 'Enter your PAN, Aadhaar and employment details. No paperwork, no branch visits.' },
        { t: 'Automated underwriting', d: 'Our engine checks eligibility in real time with our RBI-registered NBFC partner.' },
        { t: 'E-sign the agreement', d: 'Review the full cost, e-sign the loan agreement issued by the NBFC partner.' },
        { t: 'Money hits your bank', d: 'Funds move over UPI/IMPS — typically within 10 minutes of approval.' },
      ],
      bandTitle: 'Trusted when it matters most',
      bandStats: ['500,000+ Indians served', '~10-minute transfers', '₹1,00,000 max advance'],
    },
    bento: {
      eyebrow: 'Why PayDay India',
      title: 'Built for the month-end crunch.',
      cards: [
        { t: 'Instant approval engine', d: 'Real-time underwriting screens your application in seconds, 24×7 — including Sundays and bank holidays.' },
        { t: 'Zero hidden charges', d: 'One daily rate, one processing fee, 18% GST. The number you see is the number you pay.' },
        { t: 'Bank-grade security', d: '256-bit encryption, RBI-aligned data norms. Your documents never leave our vault unencrypted.' },
        { t: 'Repay your way', d: 'UPI, netbanking, debit card or autopay mandate — clear dues in two taps.' },
        { t: 'Support in English & Hindi', d: 'Real humans on chat and phone, every day of the week, 24×7.' },
      ],
      tag: ['Most popular', 'Transparent', 'Secure', 'Flexible', 'Human'],
    },
    apply: {
      eyebrow: 'Apply in 3 steps',
      title: 'Your salary advance starts here.',
      sub: 'Takes about 2 minutes. Decisions are instant; money follows right after e-sign.',
      steps: ['Loan', 'Your details', 'Review & consent'],
      step1: {
        amount: 'Loan amount',
        tenure: 'Tenure',
        purpose: 'Purpose of loan',
        purposes: ['Medical emergency', 'Home repair', 'Education', 'Travel', 'Debt payoff', 'Wedding', 'Other'],
        summary: 'Repayment summary',
        interest: 'Interest',
        fee: 'Processing fee + GST',
        total: 'Total repayment',
        due: 'Repayment due',
        next: 'Continue',
      },
      step2: {
        name: 'Full name (as per PAN)',
        phone: 'Mobile number',
        email: 'Email address',
        employment: 'Employment type',
        employments: ['Salaried — Private', 'Salaried — Government', 'Self-employed', 'Freelancer / Gig'],
        income: 'Net monthly income (₹)',
        salaryDate: 'Salary credit day (1–31)',
        pan: 'PAN number',
        aadhaar: 'Aadhaar last 4 digits',
        city: 'City (optional)',
        back: 'Back',
        next: 'Review application',
      },
      step3: {
        review: 'Application review',
        personal: 'Personal',
        loan: 'Loan',
        bank: 'Disbursal bank account',
        bankName: 'Bank name',
        account: 'Account number',
        ifsc: 'IFSC code',
        consents: [
          'I have read and accept the Terms of Service and Privacy Policy of paydayindia.com.',
          'I understand PayDay India is a technology platform and the loan will be sanctioned by an RBI-registered NBFC partner, whose key facts statement will be shared before e-sign.',
          'I consent to the collection and credit-bureau processing of my data for this application.',
        ],
        edit: 'Edit',
        submit: 'Submit application',
        submitting: 'Submitting…',
      },
      errors: {
        name: 'Enter your full name as printed on your PAN.',
        phone: 'Enter a valid 10-digit Indian mobile number.',
        email: 'Enter a valid email address.',
        income: 'Enter your net monthly income.',
        salaryDate: 'Enter the day of month your salary is credited (1–31).',
        pan: 'PAN format: ABCDE1234F.',
        aadhaar: 'Enter the last 4 digits of your Aadhaar.',
        bankName: 'Enter your bank name.',
        account: 'Enter a valid account number (9–18 digits).',
        ifsc: 'IFSC format: SBIN0001234.',
        consents: 'All three consents are required to proceed.',
      },
      success: {
        title: 'Application received!',
        sub: 'Our underwriting engine is reviewing your application. Keep your reference ID handy.',
        ref: 'Reference ID',
        status: 'Status',
        disbursal: 'Estimated disbursal',
        disbursalVal: 'Within 10 minutes of e-sign',
        new: 'Start a new application',
        notified: 'A confirmation has been sent to your email.',
      },
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Questions, answered straight.',
      items: [
        {
          q: 'What are the eligibility criteria for a PayDay India loan?',
          a: 'You must be an Indian resident aged 21–58, salaried or self-employed with a net monthly income of ₹15,000 or above, and hold a PAN card, Aadhaar and an active Indian bank account with UPI.',
        },
        {
          q: 'What documents are required to apply?',
          a: 'Just your PAN, Aadhaar and bank account details. Everything is verified digitally — no scans, printouts or office visits.',
        },
        {
          q: 'How fast will I receive the money in my bank account?',
          a: 'Most approved loans are disbursed via UPI/IMPS within 10 minutes of e-signing the agreement, 24×7 — including weekends and holidays.',
        },
        {
          q: 'Are there any prepayment penalties or hidden fees?',
          a: 'None. You can prepay anytime. You only pay 0.1% daily interest for the days you use, a one-time processing fee (2.5%, min ₹399) and 18% GST on that fee.',
        },
        {
          q: 'Is PayDay India registered with the RBI?',
          a: 'PayDay India is a digital lending platform. Loans are sanctioned by our RBI-registered NBFC lending partners, and the partner’s name, rate and key facts are disclosed in every agreement before you e-sign.',
        },
        {
          q: 'What happens if I delay my repayment?',
          a: 'Late dues attract penal interest on the outstanding amount and bureau reporting, which can affect your credit score. Write to support before your due date and we will always try to work out a plan with you.',
        },
      ],
    },
    footer: {
      tagline: 'Instant salary advances for India’s working millions — transparent, paperless and fast.',
      linksTitle: 'Explore',
      contactTitle: 'Contact',
      legalTitle: 'The legal bit',
      legal: [
        'PayDay India (paydayindia.com) is a technology platform connecting borrowers with RBI-registered NBFC partners. PayDay India is not a lender and does not make credit decisions.',
        'Representative example: ₹20,000 loan for 30 days at 0.1% daily interest — interest ₹600 + processing fee ₹500 + GST ₹90 = total repayment ₹21,190. APR (interest component, annualised): 36.5%.',
        'APR here means the annualised interest rate. Sanction is subject to partner NBFC policy, credit assessment and e-sign of the loan agreement. Borrow responsibly — short-term credit is not a solution for long-term debt.',
      ],
      rights: '© 2025 PayDay India · paydayindia.com · All rights reserved.',
      made: 'Made in India, for India.',
    },
  },

  hi: {
    nav: {
      how: 'कैसे काम करता है',
      rates: 'दरें',
      faq: 'सवाल-जवाब',
      apply: 'अभी आवेदन करें',
      track: 'ट्रैक करें',
    },
    track: {
      title: 'अपना आवेदन ट्रैक करें',
      sub: 'आवेदन के बाद साझा किया गया रेफ़रेंस ID भरें।',
      inputLabel: 'रेफ़रेंस ID',
      placeholder: 'PDI-XXXXXXXX',
      button: 'स्थिति देखें',
      checking: 'जाँच हो रही है…',
      notFound: 'इस रेफ़रेंस ID से कोई आवेदन नहीं मिला। दोबारा जाँचकर कोशिश करें।',
      appliedOn: 'आवेदन तिथि',
      amount: 'लोन राशि',
      tenure: 'अवधि',
      total: 'कुल चुकाने योग्य',
      note: 'हमारी टीम का संदेश',
      trackCta: 'आवेदन स्थिति देखें',
      rejected: 'यह आवेदन अस्वीकृत कर दिया गया है। विवरण के लिए सपोर्ट से संपर्क करें।',
      rejectedTitle: 'अस्वीकृत',
      days: 'दिन',
      steps: ['आवेदन प्राप्त', 'समीक्षा में', 'मंज़ूर', 'राशि भेज दी गई'],
      checklistTitle: 'मंज़ूर — अब क्या होगा',
      checklist: [
        'अपना लोन समझौता ई-साइन करें — सुरक्षित लिंक और OTP आपके रजिस्टर्ड मोबाइल पर आएगा (लगभग 2 मिनट)।',
        'डिस्बर्सल के लिए अपना PAN, आधार और बैंक खाता पास रखें।',
        'ई-साइन के लगभग 10 मिनट के भीतर पैसा UPI/IMPS से बैंक में।',
      ],
      checklistDocs: 'अभी कोई और दस्तावेज़ नहीं चाहिए — हमारा NBFC भागीदार कभी-कभी सैलरी स्लिप या बैंक स्टेटमेंट माँग सकता है।',
      creditedTitle: 'पैसा आपके बैंक में भेज दिए गए हैं',
      creditedAmount: 'जमा राशि',
      repayOn: 'भुगतान तिथि',
      repayMethod: 'UPI, नेटबैंकिंग, डेबिट कार्ड या ऑटोपे से चुकाएँ — दो टैप में हिसाब।',
      payNow: 'UPI से अभी भुगतान करें',
      copyVpa: 'UPI ID कॉपी करें',
      vpaCopied: 'UPI ID कॉपी हो गई!',
    },
    hero: {
      badge: 'RBI-पंजीकृत NBFC भागीदार · 100% कागज़ी-रहित',
      lines: ['तनख़्वह से पहले', 'नकद,', 'मिनटों में।'],
      sub: '₹5,000 से ₹1,00,000 तक का सैलरी एडवांस सीधे आपके बैंक खाते में। डिजिटल केवाईसी, पारदर्शी 0.1% दैनिक ब्याज, और भुगतान आपकी सैलरी डे पर।',
      ctaApply: 'लोन के लिए आवेदन करें',
      ctaHow: 'देखें कैसे काम करता है',
      estimate: 'त्वरित अनुमान',
      estimateHint: 'राशि चुनने के लिए स्लाइडर खींचें — पैसे चुकाएँ सैलरी डे पर।',
      tenureLabel: 'अवधि',
      youRepay: 'आप चुकाएँगे',
      dueOn: 'देय तिथि',
      ctaThis: 'यही राशि चाहिए',
      stats: [
        { v: '₹5K–₹1L', l: 'लोन सीमा' },
        { v: '~10 मिनट', l: 'बैंक ट्रांसफर' },
        { v: '7–90 दिन', l: 'लचीली अवधि' },
      ],
      transferChip: '₹25,000 जमा हुए · 4:32 PM',
    },
    marquee: [
      'तुरंत डिस्बर्सल',
      'बिना गारंटी',
      '100% डिजिटल केवाईसी',
      'RBI-मान्य NBFC भागीदार',
      '₹5,000 से ₹1,00,000',
      '10 मिनट में बैंक ट्रांसफर',
      'कोई छिपा शुल्क नहीं',
    ],
    rates: {
      eyebrow: 'पारदर्शी मूल्य',
      title: 'अपनी पूरी लागत पहले जानिए।',
      sub: 'एक सरल दैनिक दर — 0.1% प्रति दिन (≈36.5% वार्षिक)। भुगतान के समय कोई चौंकाने वाला शुल्क नहीं, कभी नहीं।',
      amount: 'लोन राशि',
      tenure: 'अवधि',
      days: 'दिन',
      interest: 'ब्याज (0.1% / दिन)',
      fee: 'प्रोसेसिंग शुल्क (2.5%, न्यूनतम ₹399)',
      gst: 'प्रोसेसिंग शुल्क पर GST (18%)',
      total: 'कुल चुकाने योग्य',
      due: 'भुगतान तिथि',
      apr: 'APR (ब्याज, वार्षिक)',
      note: 'RBI प्रकटीकरण हेतु उदाहरण। अंतिम स्वीकृत राशि, दर और शुल्क हमारे NBFC भागीदार द्वारा डिस्बर्सल से पहले ई-साइन समझौते में दिए जाते हैं।',
      cta: 'आवेदन पर आगे बढ़ें',
    },
    how: {
      eyebrow: 'कैसे काम करता है',
      title: 'फ़ोन से बैंक तक, चार कदमों में।',
      steps: [
        { t: 'दो-मिनट का आवेदन', d: 'PAN, आधार और रोज़गार की जानकारी भरें। न कागज़ा-कार्य, न ब्रांच जाना।' },
        { t: 'ऑटोमेटेड जाँच', d: 'हमारा इंजन हमारे RBI-पंजीकृत NBFC भागीदार के साथ तुरंत पात्रता जाँचता है।' },
        { t: 'ई-साइन करें', d: 'पूरी लागत देखकर NBFC भागीदार द्वारा जारी लोन समझौते पर ई-साइन करें।' },
        { t: 'पैसा बैंक में', d: 'राशि UPI/IMPS से आती है — आमतौर पर मंज़ूरी के 10 मिनट के भीतर।' },
      ],
      bandTitle: 'जब सबसे ज़रूरत हो, भरोसा',
      bandStats: ['5,00,000+ भारतीयों की सेवा', '~10-मिनट ट्रांसफर', '₹1,00,000 तक एडवांस'],
    },
    bento: {
      eyebrow: 'क्यों PayDay India',
      title: 'महीने के आख़िर के लिए बनाया गया।',
      cards: [
        { t: 'तुरंत मंज़ूरी इंजन', d: 'रीयल-टाइम अंडरराइटिंग सेकंडों में फ़ैसला देती है, 24×7 — रविवार और छुट्टियों में भी।' },
        { t: 'शून्य छिपे शुल्क', d: 'एक दैनिक दर, एक प्रोसेसिंग शुल्क, 18% GST। जो दिखता है, वही देना है।' },
        { t: 'बैंक-स्तरीय सुरक्षा', d: '256-बिट एन्क्रिप्शन, RBI-अनुरूप डेटा नियम। आपके दस्तावेज़ हमेशा एन्क्रिप्टेड रहते हैं।' },
        { t: 'अपने तरीक़े से भुगतान', d: 'UPI, नेटबैंकिंग, डेबिट कार्ड या ऑटोपे — दो टैप में हिसाब।' },
        { t: 'हिंदी और अंग्रेज़ी में सहायता', d: 'हफ़्ते के हर दिन, 24×7, असली इंसानों से बात।' },
      ],
      tag: ['सबसे लोकप्रिय', 'पारदर्शी', 'सुरक्षित', 'लचीला', 'व्यक्तिगत'],
    },
    apply: {
      eyebrow: '3 चरणों में आवेदन',
      title: 'आपका सैलरी एडवांस यहीं शुरू होता है।',
      sub: 'लगभग 2 मिनट लगते हैं। फ़ैसला तुरंत; ई-साइन के बाद पैसा फ़ौरन।',
      steps: ['लोन', 'आपकी जानकारी', 'समीक्षा व सहमति'],
      step1: {
        amount: 'लोन राशि',
        tenure: 'अवधि',
        purpose: 'लोन का उद्देश्य',
        purposes: ['चिकित्सा आपात', 'घर की मरम्मत', 'शिक्षा', 'यात्रा', 'क़र्ज़ चुकाना', 'शादी', 'अन्य'],
        summary: 'भुगतान सारांश',
        interest: 'ब्याज',
        fee: 'प्रोसेसिंग शुल्क + GST',
        total: 'कुल चुकाने योग्य',
        due: 'भुगतान तिथि',
        next: 'आगे बढ़ें',
      },
      step2: {
        name: 'पूरा नाम (PAN अनुसार)',
        phone: 'मोबाइल नंबर',
        email: 'ईमेल पता',
        employment: 'रोज़गार प्रकार',
        employments: ['नौकरी — प्राइवेट', 'नौकरी — सरकारी', 'स्व-रोज़गार', 'फ़्रीलांस / गिग'],
        income: 'कुल मासिक आय (₹)',
        salaryDate: 'सैलरी आने की तारीख़ (1–31)',
        pan: 'PAN नंबर',
        aadhaar: 'आधार के अंतिम 4 अंक',
        city: 'शहर (वैकल्पिक)',
        back: 'वापस',
        next: 'समीक्षा करें',
      },
      step3: {
        review: 'आवेदन समीक्षा',
        personal: 'व्यक्तिगत',
        loan: 'लोन',
        bank: 'डिस्बर्सल बैंक खाता',
        bankName: 'बैंक का नाम',
        account: 'खाता नंबर',
        ifsc: 'IFSC कोड',
        consents: [
          'मैंने paydayindia.com की सेवा शर्तें और गोपनीयता नीति पढ़ी और स्वीकार की हैं।',
          'मैं समझता/समझती हूँ कि PayDay India एक टेक्नोलॉजी प्लेटफ़ॉर्म है और लोन RBI-पंजीकृत NBFC भागीदार द्वारा स्वीकृत होता है, जिसकी मुख्य बातें ई-साइन से पहले साझा की जाएँगी।',
          'मैं इस आवेदन हेतु अपने डेटा के संग्रह और क्रेडिट-ब्यूरो प्रोसेसिंग की सहमति देता/देती हूँ।',
        ],
        edit: 'बदलें',
        submit: 'आवेदन जमा करें',
        submitting: 'जमा हो रहा है…',
      },
      errors: {
        name: 'PAN पर छपा अपना पूरा नाम भरें।',
        phone: 'मान्य 10-अंकीय भारतीय मोबाइल नंबर भरें।',
        email: 'मान्य ईमेल पता भरें।',
        income: 'अपनी कुल मासिक आय भरें।',
        salaryDate: 'सैलरी आने की तारीख़ भरें (1–31)।',
        pan: 'PAN प्रारूप: ABCDE1234F।',
        aadhaar: 'आधार के अंतिम 4 अंक भरें।',
        bankName: 'बैंक का नाम भरें।',
        account: 'मान्य खाता नंबर भरें (9–18 अंक)।',
        ifsc: 'IFSC प्रारूप: SBIN0001234।',
        consents: 'आगे बढ़ने के लिए तीनों सहमतियाँ ज़रूरी हैं।',
      },
      success: {
        title: 'आवेदन प्राप्त हुआ!',
        sub: 'हमारा अंडरराइटिंग इंजन आपका आवेदन देख रहा है। अपना रेफ़रेंस ID सुरक्षित रखें।',
        ref: 'रेफ़रेंस ID',
        status: 'स्थिति',
        disbursal: 'अनुमानित डिस्बर्सल',
        disbursalVal: 'ई-साइन के 10 मिनट के भीतर',
        new: 'नया आवेदन शुरू करें',
        notified: 'आपके ईमेल पर पुष्टि भेज दी गई है।',
      },
    },
    faq: {
      eyebrow: 'सवाल-जवाब',
      title: 'सवाल, सीधे जवाब।',
      items: [
        {
          q: 'PayDay India लोन के लिए पात्रता क्या है?',
          a: 'आप 21–58 वर्ष के भारतीय निवासी होने चाहिए, नौकरी या स्व-रोज़गार से ₹15,000+ मासिक शुद्ध आय होनी चाहिए, और पास में PAN, आधार तथा UPI-सक्रिय भारतीय बैंक खाता होना चाहिए।',
        },
        {
          q: 'आवेदन के लिए कौन-से दस्तावेज़ चाहिए?',
          a: 'बस आपका PAN, आधार और बैंक खाता विवरण। सब कुछ डिजिटल रूप से सत्यापित होता है — न स्कैन, न प्रिंटआउट, न ऑफ़िस जाना।',
        },
        {
          q: 'पैसा बैंक खाते में कितनी जल्दी आएगा?',
          a: 'ज़्यादातर स्वीकृत लोन ई-साइन के 10 मिनट के भीतर UPI/IMPS से डिस्बर्स होते हैं, 24×7 — छुट्टियों और रविवार समेत।',
        },
        {
          q: 'क्या कोई छिपा शुल्क या प्रीपेमेंट पेनल्टी है?',
          a: 'बिल्कुल नहीं। आप कभी भी आगे चुका सकते हैं। आप सिर्फ़ जितने दिन इस्तेमाल किया उसका 0.1% दैनिक ब्याज, एक बार का प्रोसेसिंग शुल्क (2.5%, न्यूनतम ₹399) और उस पर 18% GST देते हैं।',
        },
        {
          q: 'क्या PayDay India RBI से पंजीकृत है?',
          a: 'PayDay India एक डिजिटल लेंडिंग प्लेटफ़ॉर्म है। लोन हमारे RBI-पंजीकृत NBFC साझेदारों द्वारा स्वीकृत होते हैं, और साझेदार का नाम, दर व मुख्य तथ्य हर समझौते में ई-साइन से पहले बताए जाते हैं।',
        },
        {
          q: 'अगर मैं क़िस्त देर से चुकाऊँ तो?',
          a: 'देर से बकाया पर पेनल ब्याज लगता है और ब्यूरो रिपोर्टिंग होती है, जिससे आपका क्रेडिट स्कोर प्रभावित हो सकता है। देय तिथि से पहले सपोर्ट को लिखें — हम हमेशा समाधान निकालने की कोशिश करेंगे।',
        },
      ],
    },
    footer: {
      tagline: 'भारत के करोड़ों कामकाज़ी लोगों के लिए तुरंत सैलरी एडवांस — पारदर्शी, कागज़ी-रहित और तेज़।',
      linksTitle: 'देखें',
      contactTitle: 'संपर्क',
      legalTitle: 'क़ानूनी जानकारी',
      legal: [
        'PayDay India (paydayindia.com) एक टेक्नोलॉजी प्लेटफ़ॉर्म है जो उधारकर्ताओं को RBI-पंजीकृत NBFC भागीदारों से जोड़ता है। PayDay India स्वयं ऋणदाता नहीं है और क्रेडिट निर्णय नहीं लेता।',
        'प्रतिनिधि उदाहरण: ₹20,000 लोन, 30 दिन, 0.1% दैनिक ब्याज — ब्याज ₹600 + प्रोसेसिंग शुल्क ₹500 + GST ₹90 = कुल चुकाने योग्य ₹21,190। APR (ब्याज, वार्षिक): 36.5%।',
        'यहाँ APR से तात्पर्य वार्षिक ब्याज दर से है। स्वीकृति साझेदार NBFC नीति, क्रेडिट मूल्यांकन और लोन समझौते के ई-साइन पर निर्भर है। ज़िम्मेदारी से उधार लें — अल्पकालिक क्रेडिट दीर्घकालिक क़र्ज़ का समाधान नहीं है।',
      ],
      rights: '© 2025 PayDay India · paydayindia.com · सर्वाधिकार सुरक्षित।',
      made: 'भारत में बना, भारत के लिए।',
    },
  },
};
